import React from 'react';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { ILoadCreditNotesDialogProperties } from './LoadCreditNotesDialog.properties';
import { LoadCreditNotesDialogStyles } from './LoadCreditNotesDialog.styles';
import { withStyles } from '@material-ui/core/styles';
import DataGrid from 'devextreme-react/data-grid';
import 'devextreme/dist/css/dx.common.css';
import 'devextreme/dist/css/dx.light.css';
import { SUBTITLE, columns } from './LoadCreditNotesDialog.constants';
import { IFormViewModelHandle } from 'components/common/Modals/FormViewModal.properties';
import { HotKeys } from 'react-hotkeys';
import { useMemoizedKeyHandlers } from 'utils/hooks';
import { ENTER_KEY, ESCAPE, TAB } from 'utils/constants';
import DynamicDataGrid from 'components/common/DataGridDevEx/DynamicDataGrid';
import {
    useLoadInvoiceLines,
    useRetrieveCreditNotesSummary,
    useSetInvoiceLinesQueryData
} from 'api/creditNotes/creditNotes';
import { useCNContextSelector } from '../CreditNotes.context';

const LoadCreditNotesDialog = (props: ILoadCreditNotesDialogProperties) => {
    const {
        classes, open, loading, onClose, onOk, dialogStyles, invoiceLines
    } = props;

    const params = new URLSearchParams(location.search);
    const gridRef = React.useRef<DataGrid>();
    const formViewModalRef = React.useRef<IFormViewModelHandle>();
    const [selectedRows, setSelectedRows] = React.useState<any[]>([]);
    const [gridLength, setGridLength] = React.useState(0);
    const okButtonRef = React.useRef<HTMLButtonElement & { isSubmitting?: boolean }>();
    const cancelButtonRef = React.useRef<HTMLButtonElement>();
    const lastToggleButtonRef = React.useRef<HTMLInputElement>();
    const WorkCreditNoteId = useCNContextSelector<'CreditNote'>((state) => state.CreditNote) || Number(params.get('CreditNote'));
    const fetchSummary = useRetrieveCreditNotesSummary(WorkCreditNoteId);

    const loadInvoiceLinesMutation = useLoadInvoiceLines();
    const setInvoiceLinesQueryData = useSetInvoiceLinesQueryData();

    const getActions = React.useCallback(
        (_onClose, _loadCreditNotesApiCallback, _gridLength: number, selectedRowCount: number, _okButtonRef, _cancelButtonRef) => {

            return [
                {
                    title: 'Ok',
                    iconName: 'CheckCircle',
                    isDefault: (invoiceLines?.length > 0 && selectedRowCount > 0),
                    disabled: (invoiceLines?.length === 0 || selectedRowCount === 0),
                    ref: _okButtonRef,
                    listener: async () => {
                        if (_loadCreditNotesApiCallback) {
                            _loadCreditNotesApiCallback();
                        }
                    }
                },
                {
                    title: 'Cancel',
                    iconName: 'Cancel',
                    ref: _cancelButtonRef,
                    listener: () => {
                        if (_onClose) {
                            _onClose();
                        }
                    },
                },
            ];
        },
        [selectedRows, invoiceLines]
    );

    React.useEffect(
        () => {
            const gridInstance = gridRef.current?.instance;
            const data = invoiceLines;

            if (gridInstance && data?.length) {
                gridInstance.selectAll();
                setSelectedRows(data);
            }
        },
        [invoiceLines]
    );

    React.useEffect(
        () => {
            setGridLength(gridRef.current?.instance?.getDataSource()?.items().length);
        },
        [gridRef.current?.instance?.getDataSource()?.items().length, selectedRows]
    );

    React.useEffect(
        () => {
            if (!invoiceLines && invoiceLines?.data?.Forms && onClose) {
                onClose();
                setInvoiceLinesQueryData(Number(WorkCreditNoteId), {
                    Status: true,
                    CreditInvoiceLines: []
                });
            }
        },
        [invoiceLines?.data]
    );

    const loadCreditNotesApiCallback = React.useCallback(
        async () => {
            if (!selectedRows) {
                return;
            }

            okButtonRef.current.isSubmitting = true;

            const response = await loadInvoiceLinesMutation.mutateAsync({
                WorkCreditNoteId: WorkCreditNoteId,
                CreditInvoiceLines: selectedRows
            });

            okButtonRef.current.isSubmitting = false;

            if (response?.Status) {
                fetchSummary.refetch();
                if (onOk) {
                    onOk();
                }
                onClose();

                setInvoiceLinesQueryData(Number(WorkCreditNoteId), {
                    Status: true,
                    CreditInvoiceLines: []
                });
            }
        },
        [selectedRows]
    );

    const actions = React.useMemo(
        () => getActions(onClose, loadCreditNotesApiCallback, gridLength, selectedRows.length, okButtonRef, cancelButtonRef),
        [loadCreditNotesApiCallback, onClose, gridLength, selectedRows.length]
    );

    const handleOnSelectionChanged = React.useCallback(
        (e) => {
            setSelectedRows(e.selectedRowsData || []);
        },
        []
    );

    const onEscape = React.useCallback(
        () => {
            if (onClose) {
                onClose();
            }
        },
        [onClose]
    );

    const onEnter = React.useCallback(
        () => {
            if (okButtonRef.current &&
                gridRef.current.instance?.$element()?.[0]?.contains(document.activeElement)) {
                loadCreditNotesApiCallback();
            }
        },
        [okButtonRef.current, loadCreditNotesApiCallback]
    );

    const handleGridKeyDown = React.useCallback(
        (e) => {
            const keyboardEvent = e.event as KeyboardEvent;
            const dataGridInstance = gridRef.current?.instance;

            if (!dataGridInstance) return;

            const currentRowIndex = dataGridInstance.option('focusedRowIndex');
            const currentColumnIndex = dataGridInstance.option('focusedColumnIndex');

            if (currentRowIndex === undefined || currentColumnIndex === undefined) return;

            const COLUMNS_COUNT = 10;

            const focusAndScrollToCell = (rowIndex, columnIndex) => {
                const cellElement = dataGridInstance.getCellElement(rowIndex, columnIndex);
                if (cellElement) {
                    dataGridInstance.option('focusedColumnIndex', columnIndex);
                    dataGridInstance.focus(cellElement);
                    dataGridInstance.getScrollable().scrollToElement(cellElement);
                }
            };

            if (keyboardEvent.key === 'ArrowRight') {
                keyboardEvent.preventDefault();
                const nextColumnIndex = currentColumnIndex + 1;
                if (nextColumnIndex < dataGridInstance.columnCount()) {
                    focusAndScrollToCell(currentRowIndex, nextColumnIndex);
                }

                return;
            }

            if (keyboardEvent.key === 'ArrowLeft') {
                keyboardEvent.preventDefault();
                const previousColumnIndex = currentColumnIndex - 1;

                if (previousColumnIndex >= 0) {
                    if (previousColumnIndex < COLUMNS_COUNT) {
                        dataGridInstance.getScrollable().scrollTo({ x: 0 });
                    }
                    focusAndScrollToCell(currentRowIndex, previousColumnIndex);
                }

                return;
            }

            if (keyboardEvent.keyCode === TAB && keyboardEvent.shiftKey) {
                if (currentColumnIndex <= COLUMNS_COUNT) {
                    keyboardEvent.preventDefault();
                    dataGridInstance.getScrollable().scrollTo({ x: 0 });

                    return;
                }

                return;
            }

            if (keyboardEvent.keyCode === TAB && !keyboardEvent.shiftKey) {
                handleFocusToToggle();

                return;
            }

            if (keyboardEvent.keyCode === ESCAPE) {
                onEscape();

                return;
            }

            if (keyboardEvent.keyCode === ENTER_KEY) {
                onEnter();

                return;
            }
        },
        [onEscape, onEnter, formViewModalRef, gridRef]
    );

    const handleOnContentReady = React.useCallback(
        (e) => {
            const instance = e.component;

            // Prevent re-run on every re-render
            if (instance.__isSelectAllTriggered) return;
            instance.__isSelectAllTriggered = true;

            const dataSource = instance.getDataSource();

            if (!dataSource) return;

            dataSource.load().then((data) => {
                if (data && data.length > 0) {
                    instance.selectAll();
                    setSelectedRows(data);
                }
            });
        },
        []
    );

    const handlers = useMemoizedKeyHandlers({
        Escape: onEscape,
        Enter: onEnter
    });

    const handleFocusToToggle = React.useCallback(
        () => {
            setTimeout(() => lastToggleButtonRef.current?.focus(), 0);
        },
        [lastToggleButtonRef.current]
    );

    return (
        <div>
            <HotKeys handlers={handlers}>
                <FormViewModal
                    open={open}
                    dialogClassName={classes.LoadCreditNotesDialogModal}
                    title='Load Credit Note Lines'
                    subTitle={SUBTITLE}
                    loading={loading}
                    innerRef={formViewModalRef}
                    onActionShiftTabOut={handleFocusToToggle}
                    dialogStyles={dialogStyles}
                    modalContent={
                        <div className={classes.OrderRecallContainer}>
                            <DynamicDataGrid
                                dataGridClassName={classes.grid}
                                columns={columns}
                                allowHeaderFilting={false}
                                allowDelete={false}
                                actionButtonsVisibility={false}
                                standardKeyboardNavigation={false}
                                selection={'multiple'}
                                gridProps={{
                                    dataSource: invoiceLines || [],
                                    dataGridRef: gridRef,
                                    onContentReady: handleOnContentReady,
                                    onSelectionChanged: handleOnSelectionChanged,
                                    onKeyDown: handleGridKeyDown,
                                    focusedRowEnabled: true,
                                    keyExpr: 'InvoiceLineNumber'
                                }}
                            />
                        </div>
                    }
                    actions={actions}
                    dialogActionsButtons={true}
                    dialogActionsShadow={false}
                />
            </HotKeys>
        </div>
    );
};

export default withStyles(LoadCreditNotesDialogStyles)(React.memo(LoadCreditNotesDialog));
